import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Renderer2,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import Quill, { RangeStatic, Toolbar } from 'quill';
import Delta from 'quill-delta';

import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { RteImageUploadService } from '../image-upload/rte-image-upload.contract';
import { CustomToolbarOption, QuillIframeBlot, QuillImageBlot, QuillModules, QuillVideoBlot } from '../interfaces/quill.common';
import { ImageDrop } from '../rte-modules/image-drop/image-drop';
import ImageFormat from '../rte-modules/image-resizer/ImageFormat';
import ImageResize from '../rte-modules/image-resizer/ImageResize';
import MagicUrl, { CustomLinkSanitizer } from '../rte-modules/magic-url/magic-url';

import { Iframe, IframeBlot } from '@innovation-os-ng/ui/ui-quill-iframe';
import { EnrichMentionPipe, MentionBlot, UiQuillMentionContract, UiQuillMentionService } from '@innovation-os-ng/ui/ui-quill-mention';
import { TranslateService } from '@itonics/ngx-translate-simple';
import 'quill-mention';
import { debounceTime, take } from 'rxjs/operators';
import { RteToolbarOptions } from '../models/rte.model';

export interface RteContentChange {
  contentJSON: string;
  text: string;
  newImageUris?: Set<string>;
}

let rteId: number = 0;

// eslint-disable-next-line @typescript-eslint/naming-convention
declare let RTE_imageMap: { [uri: string]: Promise<string> };

const TIMEOUT_100 = 100;
const TIMEOUT_200 = 200;

@Component({
  selector: 'ui-rte',
  templateUrl: './rte.component.html',
  styleUrls: ['./rte.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RteComponent),
      multi: true
    }
  ]
})
export class RteComponent implements AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor, OnDestroy, OnInit {
  // Used to provide unique ID to placeholder image if we try to upload multiple images one after another
  public static imageLoader: number = 0;
  public isEditing: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public hasPasted: boolean = false;

  @Input()
  contentJSON: string;

  @Input()
  disabled: boolean = false;

  @Input()
  readonly: boolean = false;

  @Input()
  inline: boolean = false;
  public inlineActive: boolean = false;

  @Input()
  imageUpload: boolean = true;

  @Input()
  placeholder: string;

  @Input()
  required: boolean = false;

  @Input()
  styleOnActive: boolean = false;

  @Input()
  public toolbarOptionsInput?: RteToolbarOptions;

  @Input()
  public customToolbarOptions?: Array<CustomToolbarOption> = [];

  @Input()
  public hideToolbar: boolean = false;

  //Allow the Rte to be deactivated with an action from the parent. For example, when cancel is clicked in the comments section
  @Input()
  public onDeactivateRte?: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  //Determine when the RTE is for a comment in order to apply the correct behaviour
  @Input()
  public isComment?: boolean = false;

  //Allow toolbar to show without focusing
  @Input()
  public fixedToolbar: boolean = false;

  /**
   * Observable when trigger with true value set RTE to active state and vice-versa
   */
  @Input()
  public set activateRteStateObs(activateObs: Observable<boolean>) {
    this.setRteActiveStateObs = activateObs;

    // We have to re-listen to the observable if in case parent component changes the reference of an observable
    if (this.isRetReadyToListenForActivateStateObs) {
      this.listenToRteActivateObservable();
    }
  }

  public editorOpen: boolean = false;

  private mouseDownOverEditor: boolean = false;
  private mouseMoveDuringMouseDownOverEditor: boolean = true;
  private mouseMoveDuringMouseDownOverEditorCounter: number = 0;

  // limits
  private readonly MAX_NUMBER_MOUSE_EVENTS_PERMITED: number = 10;

  /**
   * Size should be in bytes or characters however 1000 bytes is considered 1KB
   * Also, this size includes json informations, formattings and newlines as well
   * Note: 500 bytes does not mean exact 500 characters visible in editor
   * */
  @Input()
  maxContentSize: number;

  @Input()
  mentionOptions: { disableUserMentions?: boolean; disableElementMentions?: boolean };

  // :TODO: rename this to onSubmit
  @Output()
  onContentChanged: EventEmitter<RteContentChange | boolean> = new EventEmitter<RteContentChange | boolean>();

  // Let's the outside know we have input changes that are not yet saved
  // :TODO: rename this to onContentChanged
  @Output()
  onDraftAvailable: EventEmitter<RteContentChange> = new EventEmitter<RteContentChange>();

  // emit when we have entered edit mode
  @Output()
  onEditModeEnabled: EventEmitter<boolean> = new EventEmitter<boolean>();

  // emit when we have cancelled edit mode
  @Output()
  onCancelEdit: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  onDirtyStateChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  onError: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  onRteActive: EventEmitter<boolean> = new EventEmitter<boolean>();

  //emit number of images that are being uploaded in RTE
  @Output()
  onImagesUploadingCountChange: EventEmitter<number> = new EventEmitter<number>();

  @ViewChild('rteWrapper')
  rteWrapper: ElementRef;

  public active: boolean = false;
  public editorFocus: boolean = false;

  public editorId: string = `rte_${rteId++}`;

  // Initial RTE contents
  private initialContents: string;
  private editor: Quill;

  // Used to cancel the image upload
  private imageSub: Subject<{ event: string }> = new Subject<{ event: string }>();
  private imageObs: Observable<{ event: string }> = this.imageSub.asObservable();

  private resizeObserverUnsubscribe: Subscription;

  public uploadingImages: number = 0;
  public imageLoadingCounter: number = 0;
  public isPristine: boolean = true;

  public isEmpty: boolean = true;

  public isOverflowing: boolean = false;

  public imageMap: { [uri: string]: Promise<string> } = typeof RTE_imageMap !== 'undefined' ? RTE_imageMap : {};

  private readonly placeholderURL: string = 'assets/img/loader.gif';

  private containerPreBottomOffset: number;
  private containerPostBottomOffset: number;

  private toolbarOptions = [];

  // Pre-calculates 16:9 aspect ratio of iFrame
  private aspectRatio: number = 9 / 16;

  private saveEventListenerCreated: boolean = false;

  private newUploadedUris: Set<string> = new Set<string>();

  // Range of the iframe used before and after creation
  private embedIframeRange: RangeStatic;
  // iframe properties that are added when an iframe is created
  private iframeOpts: Iframe = null;

  // Used to keep track of the container width and detect changes
  private prevWidth: number = 0;
  private activateRteSubscription: Subscription;
  private setRteActiveStateObs: Observable<boolean>;

  // We will only start to listen for state change obs for making rte activate or not after it has be properly initialize
  private isRetReadyToListenForActivateStateObs: boolean = false;

  // Function to call when the rteContent changes.
  private onModelChange = (_: RteContentChange) => {};

  constructor(
    private readonly rteImageUploadService: RteImageUploadService,
    private readonly mentionService: UiQuillMentionService,
    @Optional() private readonly mentionContract: UiQuillMentionContract,
    private renderer: Renderer2,
    private readonly translate: TranslateService
  ) {}

  ngOnInit(): void {
    if (this.fixedToolbar) {
      this.rteActivatedToggle(true);
    }
  }

  ngOnChanges(changes) {
    // Parse and assign the input value to display
    if (changes.contentJSON && this.editor) {
      this.setContent();
    }
    // Disable or enable based on input
    if ((changes.readonly || changes.disabled) && this.editor) {
      this.setState();
    }

    if (changes.mentionOptions && this.mentionOptions && this.mentionContract) {
      this.mentionService.init(this.mentionOptions);
    }

    let toolbar: Toolbar;
    try {
      toolbar = this.editor?.getModule(QuillModules.toolbar);
    } catch (error) {
      console.warn('Could not get toolbar module:', error);
      return;
    }
    if (changes.hideToolbar) {
      this.hideToolbar = changes.hideToolbar.currentValue;

      this.setRteActiveState();
    }
  }

  ngAfterViewInit() {
    this.initialContents = this.contentJSON || '{}';
    this.registerModules();
    setTimeout((_) => {
      Array.from(document.getElementsByClassName('ql-iframe')).forEach((item) => {
        item.innerHTML = '<span class="icon2-x16-world iframe-button"></span>';
      });

      this.quillInit();
      // FIXME: this is hack but we need it. Apparently even the afterViewInit hook is too early.
      // Apparently The ID is available shortly after...
      if (this.mentionContract) {
        this.mentionService.init(this.mentionOptions || {});
      }

      /*

        Because of the set timeout we may be trying to get the container even when this component gets destroyed.
        This could happen, for example when we switch tabs fast (in a programmatic way) in detail page

        To avoid undefined errors we skip manipulating the DOM if the container does not exist
      */

      if (this.editor?.container?.parentElement) {
        // Used to check if the container width changes in size to make sure that the iframe height gets adjusted according to aspect ratio
        this.resizeObserverUnsubscribe = this.resizeObservable(this.editor.container.parentElement)
          .pipe(debounceTime(500))
          .subscribe((entries: Array<ResizeObserverEntry>) => {
            for (const entry of entries) {
              // Get width
              const width = entry.borderBoxSize?.[0].inlineSize;
              // If the width has changed
              if (typeof width === 'number' && width !== this.prevWidth) {
                // Set prevWidth to current width
                this.prevWidth = width;
                // Change height of iframe container according to width
                this.maintainIframeAspectRatio();
              }
            }
          });
      }

      this.maintainIframeAspectRatio();
    }, TIMEOUT_100);

    if (this.toolbarOptionsInput) {
      //Set toolbar options to specified options in toolbarOptionsInput
      this.toolbarOptions = this.buildToolbarOptions();
    } else {
      //The default toolbar
      this.toolbarOptions = [
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ align: [] }],
        ['bold', 'italic', 'underline'],
        ['blockquote', 'code-block'],
        [{ script: 'sub' }, { script: 'super' }],
        [{ color: [] }, { background: [] }],
        ['link', 'video'],
        ['clean'],
        ['image'],
        ['iframe'],
        // eslint-disable-next-line no-unsafe-optional-chaining
        ...this.customToolbarOptions?.map((option) => [option.uri])
      ];
    }
  }

  /**
   * Creates and returns a resize observable in order for debounce to be applied
   *
   * Source link https://codeutility.org/javascript-observe-resize-of-a-dom-element-using-rxjs-stack-overflow/
   *
   * @param elem the element we are subscribing to for size changes
   */
  private resizeObservable(elem) {
    return new Observable((subscriber) => {
      const ro = new ResizeObserver((entries) => {
        subscriber.next(entries);
      });
      // Observe one or multiple elements
      ro.observe(elem);
      return () => {
        subscriber.complete();
        ro.unobserve(elem);
      };
    });
  }

  private registerModules() {
    Quill.register('modules/imageDrop', ImageDrop);
    Quill.register('modules/magicUrl', MagicUrl);
    Quill.register('modules/imageResize', ImageResize);
    Quill.register(ImageFormat, true);
    if (this.mentionContract) {
      Quill.register(MentionBlot);
    }
    Quill.register(IframeBlot, true);
    Quill.register(CustomLinkSanitizer, true);
  }

  /**
   * Handles videos as iframes so they can be resized etc
   */
  private videoHandler() {
    this.embedIFrame();
  }

  /**
   * Handles the IFrame input box that appears when entering a value into the input box
   */
  public embedIFrame() {
    // Open quill's text input
    this.editor.theme.tooltip.edit('iframe');
    let embedText: string = '';

    // Get the input box
    const input: HTMLInputElement = document.querySelector('div.ql-tooltip.ql-editing > input');

    // Get the save button in the popup
    const saveButton = document.querySelector('div.ql-tooltip.ql-editing > a.ql-action');

    // Check if there is already an event listener
    if (!this.saveEventListenerCreated) {
      // If there isn't then create them and assign true to the variable so duplicates aren't created
      this.saveEventListenerCreated = true;

      input.addEventListener('keydown', (e) => {
        this.inputSaved(embedText, e);
      });

      input.addEventListener('input', () => {
        embedText = this.editor.theme.tooltip.textbox.value;
      });

      saveButton.addEventListener('click', () => {
        this.inputSaved(embedText);
      });
    }
  }

  /**
   * Gets called when the IFrame input box that appears when entering a value into the input is submitted
   *
   * @param embedText string value of the URL or IFrame tag that needs to be embedded
   * @param e keyboard event that is triggered only when 'keydown' event occurs
   */
  private inputSaved(embedText: string, e?: KeyboardEvent) {
    // Check if key is 'Enter' (on keyboard event)
    // or if there is no KeyboardEvent (on click of the saveButton)
    if (e?.key === 'Enter' || !e) {
      try {
        this.embedIframeRange = this.editor.getSelection(true);

        const url = new URL(this.getIframeUrl(embedText));
        this.insertIframe(url.toString());
        embedText = '';
      } catch (_) {
        this.onError.emit(this.translate.instant('rte.badURL'));
        this.editor.theme.tooltip.edit('iframe');
      }
    } else {
      embedText = this.editor.theme.tooltip.textbox.value;
    }
  }

  /**
   * Handles the IFrame input box that appears when entering a value into the input box
   *
   * @param url string value of the URL or IFrame tag that needs to be embedded
   */
  private insertIframe(src: string) {
    let iframe: Iframe;
    if (this.iframeOpts) {
      iframe = this.iframeOpts;
    } else {
      iframe = {
        src
      };
    }

    // Add the IFrame to the editor
    this.editor.insertEmbed(this.embedIframeRange.index || 0, 'iframe', iframe);

    // Insert new line afrer IFrame
    this.editor.insertText(this.embedIframeRange.index + 1, '\n');

    this.editor.setSelection(this.embedIframeRange.index + 2, 0);

    if (this.editor?.container) {
      // Add the overlay (which is responsible for allowing us to resize the IFrame)
      Array.from(this.editor.container.getElementsByClassName('iframe-overlay')).forEach((element: HTMLElement) => {
        // Enable or disable the overlay which listens for clicks based on whether the editor is in edit mode or not
        if (this.editor.container.parentElement?.classList.contains('edit-mode')) {
          element.classList.remove('iframe-overlay-disabled');
        } else {
          element.classList.add('iframe-overlay-disabled');
        }
      });
    }

    this.iframeOpts = null;
  }

  /**
   * Checks to see if the text that has been inputted by the user is an iframe tag or a url
   *
   * If it is a tag, set the iframe options
   *
   * If not, then return only the URL
   *
   * @param text string value of the URL or IFrame tag that needs to be embedded
   */
  private getIframeUrl(text: string) {
    if (text.indexOf('<iframe') >= 0) {
      this.iframeOpts = {
        src: text.substring(text.indexOf('src') + 5, text.indexOf('"', text.indexOf('src') + 6)),
        title: text.indexOf('title') >= 0 ? text.substring(text.indexOf('title') + 7, text.indexOf('"', text.indexOf('title') + 7)) : ''
      };
      return this.iframeOpts.src;
    } else {
      return text;
    }
  }

  private quillInit() {
    if (!document.getElementById(this.editorId)) {
      return;
    }
    // Instantiate the RTE
    this.editor = new Quill(`#${this.editorId}`, {
      modules: {
        toolbar: this.toolbarOptions,
        imageResize: true,
        /*
                                  --> MAGIC URL <--
                Checks for URLs during typing and pasting and automatically converts them to links
                Here, true is the default value with the following regex(default)

                Regex used to check URLs during typing
                urlRegularExpression: /(https?:\/\/[\S]+)|(www.[\S]+)/

                Regex used to check URLs on paste
                globalRegularExpression: /(https?:\/\/|www\.)[\S]+/g

                We can customize (Instead of just assigning true) magicUrl with following:
                magicUrl: {
                   urlRegularExpression: regex goes here,
                   globalRegularExpression: regex goes here
                }
                */
        magicUrl: true,
        imageDrop: {
          fileHandler: this.handleFileUpload.bind(this)
        },
        ...this.mentionService.quillMentionConfig
      },
      theme: 'snow'
    });

    // The RTE container
    let containerRef: HTMLElement = this.editor?.container?.parentElement;

    try {
      // Traverse the DOM upwards until we find a parent of the RTE that is capable of scrolling
      // As a failsafe, we stop looking at the HTML tag

      // containerRef  ---->  Check if containerRef still exists
      // containerRef.scrollTop  ---->  Means that container has already been scrolled
      // containerRef.scrollHeight > containerRef.clientHeight  ---->  Means that the container is scrollable
      // containerRef instanceof HTMLHtmlElement  ---->  Make sure that we don't reach the HTML tag and try access parent
      // containerRef.classList.contains('quill')  ---->  Ensure we don't stop looking at the quill RTE
      while (
        containerRef &&
        ((!(!!containerRef.scrollTop || containerRef.scrollHeight > containerRef.clientHeight) &&
          !(containerRef instanceof HTMLHtmlElement)) ||
          containerRef.classList.contains('quill'))
      ) {
        // ^^^ this while loop keeps looking at the parent element until we find one which we can use to scroll
        containerRef = containerRef.parentElement;

        // infinite loop won't be a problem, because the RTE will be in a container which is capable
        // of displaying ALL of the RTE
        // Also, I have made sure to exit the loop once the HTML tag is reached
      }

      this.editor.scrollingContainer = containerRef;
    } catch (e) {
      // If any error occurs, just assign to the HTML tag
      this.editor.scrollingContainer = document.getElementsByTagName('html')[0];
    }

    setTimeout(() => {
      let quillToolbar;
      try {
        quillToolbar = this.editor.getModule(QuillModules.toolbar);
      } catch (error) {
        console.warn('Could not get toolbar module  ', error);
      }
      // Custom image handler
      quillToolbar?.addHandler(QuillImageBlot, () => {
        this.imageHandler();
      });

      // Custom iframe handler
      quillToolbar?.addHandler(QuillIframeBlot, () => {
        this.embedIFrame();
      });

      // Custom video handler
      quillToolbar?.addHandler(QuillVideoBlot, () => {
        this.videoHandler();
      });

      if (this.customToolbarOptions && this.customToolbarOptions.length) {
        this.customToolbarOptions.forEach((opt) => {
          Array.from(document.getElementsByClassName('ql-' + opt.uri)).forEach((item: HTMLElement) => {
            item.innerHTML = '<span class="' + opt.class + '"></span>';
            item.addEventListener('click', opt.callback);
            item.classList.add('custom-toolbar-option');
            item.setAttribute('data-after-content', opt.tooltip);
          });
        });
      }
    });

    /**
     * Function that tracks whether the mouse was over the RTE when on mouse button press.
     */
    this.editor?.root?.addEventListener('mousedown', () => {
      this.mouseDownOverEditor = true;
    });

    /**
     * Function that tracks whether the mouse moved enough over RTE while pressed to be considered a drag action.
     */
    this.editor?.root?.addEventListener('mousemove', () => {
      if (this.mouseDownOverEditor) {
        this.mouseMoveDuringMouseDownOverEditor = true;
        this.mouseMoveDuringMouseDownOverEditorCounter++;
      }
    });

    /**
     * Function considers the mouse activity while pressed down over the RTE and then applies the desired action.
     */
    this.editor?.root?.addEventListener('mouseup', (event: MouseEvent) => {
      if (
        this.mouseMoveDuringMouseDownOverEditorCounter <= this.MAX_NUMBER_MOUSE_EVENTS_PERMITED &&
        !this.inlineActive &&
        !(event.target instanceof HTMLAnchorElement)
      ) {
        this.switchEdit();
        this.activateEditorAtClickedLocation();
      }
      this.mouseDownOverEditor = false;
      this.mouseMoveDuringMouseDownOverEditorCounter = 0;
    });

    /**
     *
     * Track mousedown on toolbar format option to stop the click on an item from triggering the blur event
     */

    const toolbarItem: NodeListOf<Element> = document.querySelectorAll('.ql-formats');

    toolbarItem.forEach((item) => {
      item.addEventListener('mousedown', (event) => {
        event.preventDefault();
      });
    });

    // Parse and assign the input value to display.
    this.setContent();

    // Clear history so 'undo' doesn't remove the content initially loaded from database
    this.editor?.history?.clear();

    // Disable or enable based on input
    this.setState();
    this.isRetReadyToListenForActivateStateObs = true;
    this.listenToRteActivateObservable();

    this.editor.on('text-change', (delta, oldDelta, source) => {
      this.isEditing.next(this.editor.getText().length > 1 ? true : false);
      //We need to set onDeactivateRte back to false if at least one character is in the editor (Meaning it is active again).
      //It is empty when length is === 1
      if (this.editor.getText().length === 2) {
        this.onDeactivateRte.next(false);
      }

      let imageResizeToolbar;
      try {
        imageResizeToolbar = this.editor.getModule(QuillModules.imageResize);
      } catch (error) {
        console.warn('Failed to reposition image resize elements:', error);
      }
      imageResizeToolbar?.repositionElements();

      // TODO: Add some form of image deletion cleanup which is not handled by ImageBlot...
      // Luckily we have the rteImageUploadService available here...

      // The RTE is 'pristine' and the 'text-change' event is being triggered by a 'user' i.e. not 'api'
      if (source === 'user') {
        // Only emit the 'onDirtyStateChanged' event emitter with 'true' value if the previous state of
        this.setDirty();

        // Note that we have a unsaved Draft available
        this.onDraftAvailable.next({
          contentJSON: this.editor.getContents(),
          text: this.editor.getText(),
          newImageUris: this.newUploadedUris
        });

        // Look for loading placeholder image in RTE, if not found, enable submit button
        const currentContents: Delta = this.editor.getContents();
        if (!currentContents.ops) {
          // If ops is not defined then the Quill is behaving differently, so exit immediately.
          return;
        }
        const loadingImage = currentContents.ops.find((content: any) => {
          if (content.insert && content.insert.image) {
            return content.insert.image.indexOf(this.placeholderURL) !== -1;
          }
        });
        if (!loadingImage) {
          this.uploadingImages = 0;
          this.onImagesUploadingCountChange.emit(this.uploadingImages);
        }
        this.checkEmpty(currentContents);
      }

      if (this.maxContentSize && this.maxContentSize > 0) {
        this.isOverflowing = JSON.stringify(this.editor.getContents()).length > this.maxContentSize;
      }

      // lets update rte model
      this.updateChanges();
    });

    this.editor.on('selection-change', (range, oldRange, source) => {
      if (!range && !this.inline) {
        // On cursor exit, notify outside world that the content has changed
        this.notify();
      }
    });

    this.editor.root.addEventListener('focus', (event) => {
      this.onDeactivateRte.pipe(take(1)).subscribe((deactivate) => {
        if (deactivate) {
          this.editor.blur();
          this.rteActivatedToggle(false);
          this.onDeactivateRte.next(false);
          this.setRteActiveState();
        } else {
          this.rteActivatedToggle(true);
          this.setRteActiveState();
        }
      });
    });

    // The is a hacky fix for the selection-change event not being triggered when focus changed to
    // a button. Please have a look to following issue reported
    // to the repo
    // https://github.com/quilljs/quill/issues/1397
    this.editor.root.addEventListener('blur', () => {
      // pushing it to end of the stack to ensure the correct active element is obtained
      //Ensure click event occurs before blur with a timeout of 200
      setTimeout(() => {
        //Only apply active state changes to RTE's that are comments (In community-ng)
        if (this.isComment) {
          this.onDeactivateRte.next(false);
          this.checkIfActive();
          this.setRteActiveState();
          this.rteActivatedToggle(false);
        }

        if (
          document.activeElement.tagName.toLowerCase() === 'button' &&
          !this.rteWrapper.nativeElement.contains(document.activeElement) &&
          !this.inline
        ) {
          // execute only if the active element which caused blur is a button
          // as selection change only messes up when the blur causing element is
          // a button and its not a child of Rte editor
          this.notify();
        }

        //Paste triggers blur. Refocus editor on paste
        if (this.hasPasted) {
          this.rteActivatedToggle(true);

          this.hasPasted = false;
        }
      }, TIMEOUT_200);
    });
    if (this.placeholder && this.editor) {
      this.editor.root.dataset.placeholder = this.placeholder;
    }
  }

  // In order to show or hide submit buttons, we check if the editor is currently either
  // 1) in focus
  // or
  // 2) has content that is ready to submit
  private checkIfActive() {
    const editorText = this.editor.getText();
    this.active = editorText !== '\n' && !this.isPristine;
    this.onRteActive.emit(editorText !== '\n' && !this.isPristine);
  }

  private notify(content?: boolean) {
    if (this.disabled || this.readonly || this.isOverflowing) {
      return;
    }
    // we have no content, output to show error handling
    if (content === false) {
      this.onContentChanged.next(false);
    } else {
      // sanitize if meniton is available
      const contentJson = this.mentionContract
        ? this.mentionService.sanitizeMentions(this.editor.getContents())
        : this.editor.getContents();
      const contentChanged = !(
        (contentJson && JSON.stringify(contentJson) === this.initialContents) ||
        !JSON.parse(this.initialContents)?.['ops']?.length
      );
      this.initialContents = JSON.stringify(contentJson);
      this.onContentChanged.next({
        contentJSON: this.initialContents,
        text: this.editor.getText(),
        newImageUris: this.newUploadedUris
      });
      if (!contentChanged) {
        this.setPristine();
      }
    }
  }

  private async setContent() {
    let contentJSON: string;
    if (this.mentionContract) {
      // We add back missing details about things and users so that they can be rendered properly
      // Also, if both of the mentions are disabled, then why bother using the pipe!?
      if (!(this.mentionOptions?.disableElementMentions && this.mentionOptions?.disableUserMentions)) {
        const contentEnricherPipe = new EnrichMentionPipe(this.mentionContract);
        contentJSON = await contentEnricherPipe.transform(this.contentJSON || '{}');
      }
    }

    try {
      const input = JSON.parse(contentJSON || this.contentJSON);
      const inputDelta = new Delta(input);
      this.generateImageMap(inputDelta);
      this.checkEmpty(inputDelta);
      await this.editor.setContents(inputDelta, 'api');

      setTimeout((_) => {
        this.maintainIframeAspectRatio();
      }, TIMEOUT_100);

      if (this.editor?.container?.parentElement) {
        setTimeout((_) => {
          // Add the overlay (which is responsible for allowing us to resize the IFrame)
          Array.from(this.editor.container.getElementsByClassName('iframe-overlay')).forEach((element: HTMLElement) => {
            // Enable or disable the overlay which listens for clicks based on whether the editor is in edit mode or not
            if (this.editor.container.parentElement.classList.contains('edit-mode')) {
              element.classList.remove('iframe-overlay-disabled');
            } else {
              element.classList.add('iframe-overlay-disabled');
            }
          });
        }, 0);
      }
    } catch (e) {
      // We could use the contentJSON and assume it is something else
      // Use setText to add it to quill as text. But what if it is truly
      // JSON and just broken...?
      // this.editor.setText(this.contentJSON, 'api');
    }
  }

  private generateImageMap(input: Delta) {
    // All relative image uris
    const relativeImageUrls = input.ops
      .filter((op) => op.insert.image && !/^https?:\/\//.test(op.insert.image))
      .map((op) => op.insert.image);
    // Generate ImageMap
    relativeImageUrls.forEach((uri) => {
      this.imageMap[uri] = this.rteImageUploadService.getImageUrl(uri);
      // We are exposing a global image map to share with Quill image blot which cannot access ng services
      window['RTE_imageMap'] = this.imageMap;
    });
  }

  private checkEmpty(inputDelta: Delta) {
    // For quill, newline is the empty state.
    this.isEmpty = inputDelta.ops.length <= 1 && inputDelta.ops[0].insert === '\n';
    return this.isEmpty;
  }

  private imageHandler(): void {
    const input: HTMLInputElement = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.onchange = async () => {
      // Get bottom of container before changing its contents
      this.setContainerPreBottomOffset();

      if (!input.files) {
        return;
      }
      const file: File = input.files[0];

      this.handleFileUpload(file);
    };
    input.click();
  }

  public async handleFileUpload(file: File, retry: boolean = false) {
    let range: RangeStatic = this.editor.getSelection();
    if (!range) {
      if (retry) {
        // Retried once already. Maybe editor is disabled or some error!
        return;
      }
      setTimeout(() => {
        this.handleFileUpload(file, true);
      }, TIMEOUT_100);
      return;
    }
    this.uploadingImages++;
    this.onImagesUploadingCountChange.emit(this.uploadingImages);
    const currentImageId = RteComponent.imageLoader++;

    // While uploading multiple images one after another,
    // insert temporary loading placeholder image with unique id for each image. So that,
    // it will be possible for us to trace the loader and update RTE
    await this.editor.insertEmbed(range.index, QuillImageBlot, `${this.placeholderURL}?id=${currentImageId}`);
    await this.waitForImageToLoad(this.placeholderURL);

    // Set cursor position (so it does not jump to image after upload)
    this.editor.setSelection(range.index + 1, 0);

    try {
      // Get uploaded image URL
      const imageUrl: string = await this.rteImageUploadService.uploadFile(file, this.imageObs);

      this.newUploadedUris.add(imageUrl);

      const currentContent: Delta = this.editor.getContents();

      const placeholder = currentContent.ops.find(
        (content: any) => content.insert && content.insert.image && content.insert.image === `${this.placeholderURL}?id=${currentImageId}`
      );
      if (placeholder) {
        placeholder.insert.image = imageUrl;

        // Get range so we know where the cursor is before we add the image
        range = this.editor.getSelection();

        // Add the image to the editor
        this.contentJSON = JSON.stringify(currentContent);
        this.setContent();
        this.waitForImageToLoad(imageUrl);
      }
      this.setDirty();

      // Note that we have a unsaved Draft available
      this.onDraftAvailable.next({
        contentJSON: this.editor.getContents(),
        text: this.editor.getText(),
        newImageUris: this.newUploadedUris
      });
    } catch (error) {
      // Get range so we know where the cursor is before we add the image
      range = this.editor.getSelection();

      // If image upload process is rejected, remove the loading placeholder image and update the rte contents
      const currentContent: Delta = this.editor.getContents();

      // Add the image to the editor
      this.editor.setContents(
        currentContent.ops.filter(
          (content: any) =>
            !(content.insert && content.insert.image && content.insert.image === `${this.placeholderURL}?id=${currentImageId}`)
        )
      );
    }
    if (this.uploadingImages > 0) {
      this.uploadingImages--;
      this.onImagesUploadingCountChange.emit(this.uploadingImages);
    }

    if (range && range?.index) {
      // cursorPos is used to set the cursor to the position it was before adding the image
      const cursorPos: number = range.index;

      // Set cursor position (so it does not jump to image after upload)
      this.editor.setSelection(cursorPos, 0);
    }
  }

  /**
   * Creates an event handler for the image that has been uploaded
   * This is necessary because the height of the RTE will only change when the
   * image has been loaded
   *
   * When the image is loaded in the DOM, then we readjust the scroll of the RTE
   *
   * @param url string url that the uploaded image will have
   */
  private waitForImageToLoad(url: string) {
    let tempUrl = url;

    // This is necessary because the loader increments every time it is added and is
    // set back to = on a page refresh. This is appended to the end of the placeholderURL
    if (tempUrl === this.placeholderURL) {
      tempUrl = this.placeholderURL + `?id=${this.imageLoadingCounter++}`;
    }
    document.querySelectorAll('img[src="' + tempUrl + '"]')[0]?.addEventListener(
      'load',
      function onImageLoad() {
        // Get the offset of the container after the image is uploaded
        this.setContainerPostBottomOffset();

        // Scroll so that the bottom of the container is where it started
        this.scrollToCursor();

        // Remove the event listener after executing the logic because it is no longer necessary
        removeEventListener('load', onImageLoad, false);
      }.bind(this),
      false
    );
  }

  /**
   * Sets the bottom of the container, so we know what to return it to
   * when the image has been inserted
   */
  private setContainerPreBottomOffset() {
    this.containerPreBottomOffset = this.editor.container.getBoundingClientRect().bottom;
  }

  /**
   * Sets the bottom of the container, so we are able to calculate
   * the change in height to rectify it and scroll the cursor back into view
   */
  private setContainerPostBottomOffset() {
    this.containerPostBottomOffset = this.editor.container.getBoundingClientRect().bottom;
  }

  /**
   * Sets the bottom of the RTE container to the same position it was before uploading
   * the image
   */
  private scrollToCursor() {
    // The RTE container
    let containerRef: HTMLElement | null = this.editor.container.parentElement;

    // The amount the container has moved as a result of the image upload
    const delta: number = this.containerPostBottomOffset - this.containerPreBottomOffset || 0;

    // containerRef.scrollHeight > containerRef.clientHeight  ---->  means that the container is scrollable
    // and
    // containerRef.scrollHeight - containerRef.clientHeight > delta
    //                                                       ^
    //                                                       |
    //                                                       |
    // means that it can be scrolled enough to accommodate the change in size because of the image upload
    while (
      containerRef &&
      !(containerRef.scrollHeight > containerRef.clientHeight && containerRef.scrollHeight - containerRef.clientHeight > delta)
    ) {
      // ^^^ this while loop keeps looking at the parent element until we find one which we can use to scroll
      containerRef = containerRef.parentElement;

      // infinite loop won't be a problem, because the RTE will be in a container which is capable
      // of displaying ALL of the RTE
    }

    // Make sure containerRef is assigned
    if (containerRef) {
      // Increase the scrollTop by the required amount to ensure the RTE does not jump
      containerRef.scrollTop = containerRef.scrollTop + delta;
    }
  }

  private setState() {
    if (!this.inline) {
      this.inlineActive = true;
    }
    if (this.disabled || this.readonly) {
      this.inlineActive = false;
    }

    if (this.editor?.container) {
      // Add the overlay (which is responsible for allowing us to resize the IFrame)
      Array.from(this.editor.container.getElementsByClassName('iframe-overlay')).forEach((element: HTMLElement) => {
        if (this.readonly || (this.inline && !this.inlineActive)) {
          element.classList.add('iframe-overlay-disabled');
        } else {
          element.classList.remove('iframe-overlay-disabled');
        }
      });
    }

    let imageResizeToolbar;
    try {
      imageResizeToolbar = this.editor.getModule(QuillModules.imageResize);
    } catch (error) {
      console.warn('Could not get imageResize module:', error);
    }
    if (imageResizeToolbar) {
      imageResizeToolbar.hideOverlay();
      imageResizeToolbar.hideResizer = !this.inlineActive;
    }
    if (this.editor) {
      this.editor.enable(this.inlineActive);
    }
    let toolbar: Toolbar;
    try {
      toolbar = this.editor?.getModule(QuillModules.toolbar);
    } catch (error) {
      console.warn('Could not get toolbar module:', error);
      return;
    }

    this.setRteActiveState();

    const getHandler = (action) => {
      // FIX CLOUD-2286
      // for the image action we need to keep using our Service to upload the image
      // not the editors default which we would reapply if we used the toolbar options
      if (action === QuillImageBlot) {
        return () => {
          this.imageHandler();
        };
      } else if (action === QuillIframeBlot || action === QuillVideoBlot) {
        return () => {
          this.embedIFrame();
        };
      }
      return toolbar.options.handlers[action];
    };

    Object.keys(toolbar.options.handlers).forEach((action) => {
      toolbar.addHandler(action, this.disabled || this.readonly ? () => {} : getHandler(action));
    });

    if (this.disabled || this.readonly) {
      toolbar.container.classList.add('toolbar-disabled');
    } else {
      toolbar.container.classList.remove('toolbar-disabled');
    }
  }

  // inline
  public switchEdit() {
    Array.from(document.getElementsByClassName('ql-iframe')).forEach((item) => {
      item.innerHTML = '<span class="icon2-x16-world iframe-button"></span>';
    });
    if (this.inline) {
      this.inlineActive = true;
      this.setState();
      this.onEditModeEnabled.next(true);
      this.maintainIframeAspectRatio();
    }
  }

  public inlineSubmit() {
    if (this.uploadingImages || this.isOverflowing) {
      return;
    }

    // check if we have input
    const text = this.editor.getText().trim();
    if (this.required && !text) {
      // no input return true
      this.notify(false);
    } else {
      this.inlineActive = false;
      this.setState();
      this.notify();
      this.contentJSON = this.initialContents;
      this.setContent();
    }
    this.maintainIframeAspectRatio();
    this.rteActivatedToggle(false);
  }

  /**
   * This funtion ensures that the cursor will be at the end of the inserted text
   * and that the cursor will remain in view when when pasting text inside the RTE.
   *
   * @param event The paste (ClipboardEvent) event that needs to cater for Quill properties that
   * are added due to it originating from the Quilljs editor.
   */
  private scrollToBottomAfterPaste(event: ClipboardEvent | Quill) {
    // Get current selection
    const selection: Selection = window.getSelection();

    // Check if there are selection ranges
    if (!selection.rangeCount) {
      return;
    }

    selection.focusNode.parentElement.scrollIntoView({
      block: 'center',
      behavior: 'auto'
    });
  }

  public inlineCancel() {
    this.imageSub.next({ event: 'cancel' });
    this.inlineActive = false;

    this.rteActivatedToggle(false);

    //Hide toolbar on cancel
    let toolbar: Toolbar;
    try {
      toolbar = this.editor?.getModule(QuillModules.toolbar);
    } catch (error) {
      console.warn('Could not get toolbar module:', error);
      return;
    }

    this.editor.getModule(QuillModules.toolbar);
    toolbar.container.hidden = true;

    this.onCancelEdit.next(true);
    this.setState();
    // reset the content
    this.contentJSON = this.initialContents;
    this.setContent();
    this.uploadingImages = 0;
    this.onImagesUploadingCountChange.emit(this.uploadingImages);
    this.setPristine();
    this.notify(false);
    this.maintainIframeAspectRatio();
  }

  /**
   * Return height calculated according to 16:9 aspect ratio based on width
   */
  public calculateHeight(width: number): number {
    return this.aspectRatio * width;
  }

  /**
   * Ensures that after either saving or cancelling changes to the editor, don't make
   * Iframes resize incorrectly
   **/
  private maintainIframeAspectRatio() {
    setTimeout((_) => {
      // Change height of iframe container according to width
      document.querySelectorAll('.iframe-container').forEach((container: HTMLElement) => {
        container.style['height'] = this.calculateHeight(container.getBoundingClientRect().width) + 'px';
      });
    }, TIMEOUT_100);
  }

  private setPristine() {
    if (!this.isPristine) {
      this.isPristine = true;
      this.onDirtyStateChanged.emit(false);
      this.newUploadedUris = new Set<string>();
    }
  }

  private setDirty() {
    if (this.isPristine) {
      this.isPristine = false;
      this.onDirtyStateChanged.emit(true);
    }
  }

  ngOnDestroy() {
    this.imageSub.unsubscribe();

    this.activateRteSubscription?.unsubscribe();
  }

  private updateChanges(): void {
    // sanitize if meniton is available
    const contentJson = this.mentionContract ? this.mentionService.sanitizeMentions(this.editor.getContents()) : this.editor.getContents();
    this.onModelChange({
      contentJSON: JSON.stringify(contentJson),
      text: this.editor.getText()
    });
  }

  /*
   * Updates rte modal with new value.
   *
   * This method will be called by the forms API to write to the view when programmatic
   * (model -> view) changes are requested.
   *
   *
   */
  public writeValue(rteContent: RteContentChange): void {
    if (rteContent && rteContent.contentJSON) {
      this.contentJSON = rteContent.contentJSON;
      this.setContent();
    }
  }

  /*
   * Allows Angular to register a function to call when the model changes.
   * Grab the function as a property to trigger it every time the change in selected items occurs.
   */
  public registerOnChange(fn: any): void {
    this.onModelChange = fn;
  }

  /*
   * Allows Angular to register a function to call when the input has been touched.
   * Save the function as a property to call later here.
   *
   * Here for this wrapper component we do not care if the user has interacted or not so we leave
   * registerOnTouched ALONE !!
   *
   */
  public registerOnTouched(fn: any): void {}

  /**
   * Finds the exact click index of the string under the mouse pointer and then opens the
   * editor with the cursor set to that location.
   */
  private activateEditorAtClickedLocation() {
    const clickSelection: Selection = window.getSelection();
    const currentNode = Quill?.find(clickSelection.focusNode);

    if (!!currentNode) {
      const currentNodeStartIndex = this.editor.getIndex(currentNode);
      const clickedIndex = currentNodeStartIndex + clickSelection.focusOffset;
      this.editor.setSelection(clickedIndex, 0);
    }
  }

  /**
   * Build toolbar options array based on toolbarOptionsInput
   *
   * @returns array of toolbar options
   */

  private buildToolbarOptions() {
    const header = this.toolbarOptionsInput.header ? [1, 2, 3, 4, 5, 6, false] : undefined;
    const listOrdered = this.toolbarOptionsInput.listOrdered ? 'ordered' : undefined;
    const listBullet = this.toolbarOptionsInput.listBullet ? 'bullet' : undefined;
    const positiveIndent = this.toolbarOptionsInput.indent ? '+1' : undefined;
    const negativeIindent = this.toolbarOptionsInput.indent ? '-1' : undefined;
    const align = this.toolbarOptionsInput.align ? [] : undefined;
    const bold = this.toolbarOptionsInput.bold ? 'bold' : undefined;
    const italic = this.toolbarOptionsInput.italic ? 'italic' : undefined;
    const underline = this.toolbarOptionsInput.underline ? 'underline' : undefined;
    const blockquote = this.toolbarOptionsInput.blockquote ? 'blockquote' : undefined;
    const codeBlock = this.toolbarOptionsInput.codeBlock ? 'code-block' : undefined;
    const subcript = this.toolbarOptionsInput.subScript ? 'sub' : undefined;
    const superScript = this.toolbarOptionsInput.superScript ? 'super' : undefined;
    const color = this.toolbarOptionsInput.color ? [] : undefined;
    const background = this.toolbarOptionsInput.background ? [] : undefined;
    const link = this.toolbarOptionsInput.link ? 'link' : undefined;
    const video = this.toolbarOptionsInput.video ? 'video' : undefined;
    const clean = this.toolbarOptionsInput.clean ? 'clean' : undefined;
    const image = this.toolbarOptionsInput.image ? 'image' : undefined;
    const iframe = this.toolbarOptionsInput.iframe ? 'iframe' : undefined;

    const options = [
      [{ header }],
      [{ list: listOrdered }, { list: listBullet }],
      [{ indent: positiveIndent }, { indent: negativeIindent }],
      [{ align }],
      [bold, italic, underline],
      [blockquote, codeBlock],
      [{ script: subcript }, { script: superScript }],
      [{ color }, { background }],
      [link, video],
      [clean],
      [image],
      [iframe]
    ];

    return options;
  }

  /**
   * Set RteActiveState based on whether user is editing or not
   *
   */

  private setRteActiveState() {
    //Only apply changes if toolbar isn't fixed

    this.isEditing.pipe(take(1)).subscribe((isEditing) => {
      if (!isEditing) {
        this.rteActivatedToggle(false);
      } else {
        this.rteActivatedToggle(true);
      }
    });
  }

  /**
   * Activate or deactivate RTE
   *
   * @param active
   * @param focus
   */

  public rteActivatedToggle(active: boolean, focus: boolean = false): void {
    let toolbar: Toolbar;
    try {
      toolbar = this.editor?.getModule(QuillModules.toolbar);
    } catch (error) {
      console.warn('Could not get toolbar module:', error);
      return;
    }
    //Cancel the toggle if there is a fixed toolbar
    if (!active && this.fixedToolbar) {
      toolbar.container.hidden = false;
    } else {
      this.onRteActive.emit(active);
      this.isEditing.next(active);
      this.active = active;
      this.editorFocus = active;

      // If both of the active and focus is true, we will focus the editor
      if (focus && active) {
        this.editor.focus();

        // Used setTimeout so that, editor instance get all the content we passed as contentJSON and move cursor to the end
        setTimeout(() => {
          this.editor.setSelection(this.editor.getLength(), 0);
        });
      }
      if (toolbar) {
        toolbar.container.hidden = !active;
      }
    }
  }

  /**
   * Handle paste event
   *
   */

  public handlePaste() {
    this.hasPasted = true;
  }

  /**
   * Listen to rte activate state obs
   */
  private listenToRteActivateObservable(): void {
    if (this.setRteActiveStateObs) {
      this.activateRteSubscription?.unsubscribe();
      this.activateRteSubscription = this.setRteActiveStateObs.subscribe({
        next: (isActive) => {
          this.rteActivatedToggle(isActive, true);
        }
      });
    }
  }
}
